/**
 * JS heap sampling → perf ingest kind="memory" (web analog of Tracer's leak watcher).
 * Uses Chrome's non-standard performance.memory; other browsers are skipped silently.
 * A stable per-page session id ties samples into a series the collector analyzes for
 * leak-like monotonic growth; jsHeapSizeLimit rides along so it can also flag
 * "memory at the limit".
 */
import type { ResolvedConfig } from './config.js';
import { uuid } from './util.js';
import { sendPerfEvents } from './perfsend.js';

interface ChromeMemory {
  usedJSHeapSize: number;
  jsHeapSizeLimit: number;
}

function heap(): ChromeMemory | undefined {
  return (performance as unknown as { memory?: ChromeMemory }).memory;
}

export function installMemorySampling(cfg: ResolvedConfig): () => void {
  if (typeof window === 'undefined' || !cfg.captureMemory || !heap()) return () => {};

  const sessionId = cfg.sessionId ?? `pg-${uuid().slice(0, 8)}`;
  const sample = (beacon = false) => {
    const m = heap();
    if (!m) return;
    sendPerfEvents(cfg, [{
      id: uuid(),
      project_id: cfg.projectId,
      environment: cfg.environment,
      kind: 'memory',
      name: 'js_heap_used',
      value: m.usedJSHeapSize,
      route: location.pathname,
      session_id: sessionId,
      tags: { runtime: 'browser', limit: m.jsHeapSizeLimit },
      timestamp: new Date().toISOString(),
    }], beacon);
  };

  const timer = setInterval(() => sample(), cfg.memorySampleMs);
  sample(); // baseline right away so short sessions still contribute a starting point
  const onHide = () => {
    if (document.visibilityState === 'hidden') sample(true);
  };
  document.addEventListener('visibilitychange', onHide);

  return () => {
    clearInterval(timer);
    document.removeEventListener('visibilitychange', onHide);
  };
}
